import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import './Navigation.css';

const Navigation = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(false);
  const { i18n, t } = useTranslation();

  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-50% 0px',
      threshold: 0
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    const sections = document.querySelectorAll('section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const handleNavClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const changeLanguage = async (lang) => {
    if (lang === i18n.language) return;
    
    setIsLoading(true);
    try {
      await i18n.changeLanguage(lang);
      localStorage.setItem('preferredLanguage', lang);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const navItems = [
    { id: 'home', label: t('navigation.menu.home') },
    { id: 'about', label: t('navigation.menu.about') },
    { id: 'experience', label: t('navigation.menu.experience') },
    { id: 'skills', label: t('navigation.menu.skills') },
    { id: 'projects', label: t('navigation.menu.projects') },
    { id: 'contact', label: t('navigation.menu.contact') }
  ];

  return (
    <nav>
      <div className="nav-container">
        <div className="logo-container">
          <div className="nav-logo" onClick={() => handleNavClick('home')}>
            <span className="logo-text">S</span>
          </div>
        </div>
        <div className="menu-container">
          <ul className="nav-list">
            {navItems.map(({ id, label }) => (
              <li key={id}>
                <button
                  className={`nav-item ${activeSection === id ? 'active' : ''}`}
                  onClick={() => handleNavClick(id)}
                  dangerouslySetInnerHTML={createMarkup(label)}
                />
              </li>
            ))}
          </ul>
        </div>
        <div className="language-container">
          <button
            className={`language-button ${i18n.language === 'en' ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={() => changeLanguage('en')}
            disabled={isLoading}
            aria-label={t('navigation.language.en')}
          >
            EN
          </button>
          <button
            className={`language-button ${i18n.language === 'ja' ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={() => changeLanguage('ja')}
            disabled={isLoading}
            aria-label={t('navigation.language.ja')}
          >
            日本語
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;